import React, { useContext, useEffect, useState, useMemo } from "react";
import { getShiftDefinitions } from "../../apis/shiftDefinition";
import { getShiftAssignments, deleteShiftAssignment } from "../../apis/shiftAssignment";
import AddShiftDefinition from "./AddShiftDefinition";
import AssignShiftModal from "./AssignShiftModal";
import WhosOnDutyToday from "./WhosOnDutyToday";
import Modal from "../../components/Modal";
import { LanguageContext } from "../../providers/LanguageProvider";

const DutyRoster = () => {
  const [shifts, setShifts] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [editableShiftId, setEditableShiftId] = useState<number | null>(null);
  const [isShiftModalOpen, setShiftModalOpen] = useState(false);
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const { translate: t } = useContext(LanguageContext);

  const fetchShifts = () => {
    getShiftDefinitions({ per_page: 100 }).then((res: any) => {
      setShifts(res.data?.data || res.data || []);
    });
  };

  const fetchAssignments = () => {
    getShiftAssignments({ per_page: 100 }).then((res: any) => {
      setAssignments(res.data?.data || res.data || []);
    });
  };

  useEffect(() => {
    fetchShifts();
    fetchAssignments();
  }, []);

  const handleCloseShiftModal = () => {
    setEditableShiftId(null);
    setShiftModalOpen(false);
    fetchShifts();
  };

  const handleAssignSuccess = () => {
    fetchAssignments();
  };

  const handleDeleteAssignment = (id: number) => {
    if (window.confirm("Remove this assignment?")) {
      deleteShiftAssignment(id).then(() => {
        fetchAssignments();
      });
    }
  };

  const getUserName = (a: any) => {
    const u = a.user || a;
    if (!u) return "-";
    return `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.trim() || u.email || "-";
  };

  const getShiftName = (a: any) => {
    const s = a.shiftDefinition || a.shift_definition || a;
    if (!s) return "-";
    return s.name || "-";
  };

  const getShiftTime = (a: any) => {
    const s = a.shiftDefinition || a.shift_definition || a;
    if (!s) return "";
    const st = s.startTime || s.start_time || "";
    const et = s.endTime || s.end_time || "";
    return st && et ? `${st} - ${et}` : "";
  };

  // Derive current shift (whose time range includes now) for "Who's On Duty" display
  const currentShift = useMemo(() => {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const today = now.toISOString().slice(0, 10);
    for (const s of shifts) {
      const st = s.startTime || s.start_time || "";
      const et = s.endTime || s.end_time || "";
      if (!st || !et) continue;
      const [sh, sm] = st.split(":").map(Number);
      const [eh, em] = et.split(":").map(Number);
      const startM = sh * 60 + (sm || 0);
      const endM = eh * 60 + (em || 0);
      if (nowMinutes >= startM && nowMinutes <= endM) {
        return { name: s.name, startTime: st, endTime: et };
      }
    }
    return shifts[0]
      ? {
          name: shifts[0].name,
          startTime: shifts[0].startTime || shifts[0].start_time || "",
          endTime: shifts[0].endTime || shifts[0].end_time || "",
        }
      : null;
  }, [shifts]);

  // Build present-by-role from today's assignments (sample UI - wire to real user roles when available)
  const presentByRole = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todaysAssignments = assignments.filter((a) => a.date === today);
    const byRole: Record<string, { id: number; name: string }[]> = {};
    for (const a of todaysAssignments) {
      const u = a.user || a;
      const name = getUserName(a);
      const roles = (u?.roles || []) as string[];
      const roleKey = roles[0]?.toLowerCase().replace(/\s+/g, "_") || "other";
      if (!byRole[roleKey]) byRole[roleKey] = [];
      if (u?.id && !byRole[roleKey].some((x) => x.id === u.id)) {
        byRole[roleKey].push({ id: u.id, name });
      }
    }
    return byRole;
  }, [assignments]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex-auto">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          {t("duty_roster") || "Duty Roster"}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage shift definitions and assign shifts to users.
        </p>
      </div>

      {/* Who's On Duty Today - sample UI */}
      <WhosOnDutyToday currentShift={currentShift} presentByRole={presentByRole} />

      {/* Shift Definitions */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-900">Shift Definitions</h2>
          <button
            type="button"
            onClick={() => setShiftModalOpen(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Add Shift
          </button>
        </div>
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  {t("name")}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Start Time
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  End Time
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t("actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shifts.length > 0 ? (
                shifts.map((s) => (
                  <tr key={s.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {s.name}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {s.startTime || s.start_time || "-"}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {s.endTime || s.end_time || "-"}
                    </td>
                    <td className="px-3 py-4">
                      <button
                        onClick={() => {
                          setEditableShiftId(s.id);
                          setShiftModalOpen(true);
                        }}
                        className="text-sm font-semibold text-primary hover:text-hover"
                      >
                        {t("edit")}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                    No shifts defined. Add a shift to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Shift Assignments */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-900">Shift Assignments</h2>
          <button
            type="button"
            onClick={() => setAssignModalOpen(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Assign Shift
          </button>
        </div>
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  User
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Shift
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Date
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t("actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assignments.length > 0 ? (
                assignments.map((a) => (
                  <tr key={a.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {getUserName(a)}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {getShiftName(a)} {getShiftTime(a) && `(${getShiftTime(a)})`}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {a.date || "-"}
                    </td>
                    <td className="px-3 py-4">
                      <button
                        onClick={() => handleDeleteAssignment(a.id)}
                        className="text-sm font-semibold text-red-600 hover:text-red-500"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                    No assignments yet. Assign a shift to a user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isShiftModalOpen && (
        <Modal
          title={editableShiftId ? "Edit Shift" : "Add Shift"}
          onClose={handleCloseShiftModal}
          modalContent={
            <AddShiftDefinition
              editableId={editableShiftId}
              closeModal={handleCloseShiftModal}
            />
          }
        />
      )}

      {isAssignModalOpen && (
        <Modal
          title="Assign Shift"
          onClose={() => setAssignModalOpen(false)}
          modalContent={
            <AssignShiftModal
              onClose={() => setAssignModalOpen(false)}
              onAssigned={handleAssignSuccess}
            />
          }
        />
      )}
    </div>
  );
};

export default DutyRoster;
